import React from "react";
import { Check, X, ShieldCheck, Eye, Zap, Activity } from "lucide-react";
import { Container, SectionHeading, PrimaryLink, SecondaryLink } from "@/components/ui-kit";
import { WHAT_WE_HANDLE, WHAT_WE_DONT, SITE } from "@/data/content";
import { useRegion } from "@/hooks/useRegion";
import { isTagalogSelected } from "@/data/tagalogTranslations";

const APPROACH = [
  { icon: Zap, title: "Proactive", text: "Problems are prevented, not chased." },
  { icon: Eye, title: "Transparent", text: "You know what's handled and what isn't." },
  { icon: Activity, title: "Reliable", text: "Your site works when it needs to." },
  { icon: ShieldCheck, title: "Calm", text: "No jargon. No urgency theater." },
];

export default function About() {
  const { isPH, link } = useRegion();
  const isTL = isPH && isTagalogSelected();
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <Container className="py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-[#10B981] mb-3">
                {isTL ? `Tungkol sa ${SITE.name}` : <>About {SITE.name}</>}
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold font-display text-[#0F172A] text-balance">
                What this business exists to do
              </h1>
              <p className="mt-6 text-lg text-[#475569] leading-relaxed">
                MySiteCare exists to make website responsibility clear. A website is business infrastructure. Before
                work begins, we define what MySiteCare handles, what remains with you, and who you contact when
                something goes wrong.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <PrimaryLink to={link("/contact")} testId="about-cta-contact">
                  Tell Us About Your Website
                </PrimaryLink>
                <SecondaryLink to={link("/plans")} testId="about-cta-plans">
                  View Plans
                </SecondaryLink>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg border border-[#E2E8F0]">
              <img src="/images/about.png" alt="About MySiteCare" className="w-full h-full object-cover" />
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 bg-[#F8FAFC] border-y border-[#E2E8F0]">
        <Container className="max-w-4xl">
          <SectionHeading eyebrow="Who operates MySiteCare" title="A real business with a named operator" />
          <p className="text-lg text-[#475569] leading-relaxed">
            MySiteCare is operated by Jeanjen Sisoy Luberio under the registered business name MySiteCare Website
            Application Services, a Philippine sole proprietorship. DTI Business Name No. 7934250.
          </p>
          <p className="mt-4 text-[#475569] leading-relaxed">
            {isPH
              ? "The Philippines service track serves businesses across the Philippines."
              : "MySiteCare primarily serves business customers in the Philippines and also offers digital website services to business customers in Europe and other international markets."}
          </p>
          <div className="mt-7">
            <SecondaryLink to={link("/business-information")} testId="about-business-registration">
              View Business Registration
            </SecondaryLink>
          </div>
        </Container>
      </section>

      {/* POSITION */}
      <section className="py-20 md:py-24 bg-white">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <SectionHeading eyebrow="Our position" title="Read this carefully" />
              <div className="space-y-4 text-[#475569] leading-relaxed">
                <p>We are not a marketing agency. We are not a growth service. We do not sell promises.</p>
                <p>
                  MySiteCare provides website builds with ownership handover, maintenance, hosting, monitoring,
                  covered fixes, and responsibility services with clearly defined boundaries.
                </p>
                <p className="text-[#0F172A] font-medium">
                  No marketing promises. No technical smoke and mirrors. No vague unlimited scope. Just clearly defined responsibility.
                </p>
              </div>
            </div>
            <div className="rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0] p-8">
              <h3 className="text-xl font-semibold font-display text-[#0F172A]">Who MySiteCare is for</h3>
              <p className="mt-4 text-[#475569] leading-relaxed">
                MySiteCare is built for business owners who value calm and delegation. People who want one thing:
              </p>
              <blockquote className="mt-4 border-l-4 border-[#10B981] pl-4 text-lg italic text-[#0F172A]">
                “I don't want to manage this. I want it handled.”
              </blockquote>
              <p className="mt-4 text-[#475569] leading-relaxed">
                If your website matters to your business — but you don't want to babysit it — this is for you. This
                service is deliberately kept small so responsibility doesn't get diluted.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* WHY THIS EXISTS */}
      <section className="py-20 md:py-24 bg-[#0F172A]" data-testid="why-this-exists">
        <Container>
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-[#34D399] mb-3">
              Why this exists
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-display text-white text-balance">
              Most website problems are not complex. They're neglected.
            </h2>
            <div className="mt-6 space-y-4 text-slate-300 leading-relaxed">
              <p>Updates don't get done. Backups aren't tested. Small issues pile up until something breaks — usually at the worst moment.</p>
              <p>MySiteCare was created to prevent that. We don't wait for problems. We remove the conditions that cause them.</p>
            </div>
          </div>
        </Container>
      </section>

      {/* WHAT WE TAKE RESPONSIBILITY FOR */}
      <section className="py-20 md:py-24 bg-white">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-8 shadow-sm">
              <h3 className="text-xl font-semibold font-display text-[#0F172A]">What we take responsibility for</h3>
              <p className="mt-1 text-sm text-[#475569]">Responsibility depends on the selected plan and accepted scope.</p>
              <div className="mt-6 space-y-3">
                {WHAT_WE_HANDLE.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="rounded-full bg-[#10B981]/10 p-1 mt-0.5">
                      <Check size={14} className="text-[#10B981]" />
                    </span>
                    <span className="text-sm text-[#1E293B]">{item}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm text-[#0F172A] font-medium">Clear scope. Clear boundaries. No surprises.</p>
            </div>

            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-8">
              <h3 className="text-xl font-semibold font-display text-[#0F172A]">What we deliberately do not do</h3>
              <p className="mt-1 text-sm text-[#475569]">We do not promise:</p>
              <div className="mt-6 space-y-3">
                {WHAT_WE_DONT.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="rounded-full bg-slate-200 p-1 mt-0.5">
                      <X size={14} className="text-[#64748B]" />
                    </span>
                    <span className="text-sm text-[#475569]">{item}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm text-[#1E293B] font-medium">We handle the website work assigned to us. We do not run your business.</p>
            </div>
          </div>
        </Container>
      </section>

      {/* HOW WE WORK */}
      <section className="py-20 md:py-24 bg-[#F8FAFC]">
        <Container>
          <SectionHeading
            eyebrow="How we work"
            title="Intentionally simple"
            subtitle="We deliberately work with a limited number of websites so responsibility doesn't get diluted. This is not scale-at-all-costs. It's controlled, accountable service."
            center
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {APPROACH.map((a) => (
              <div key={a.title} className="rounded-2xl border border-[#E2E8F0] bg-white p-7 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                <div className="inline-flex rounded-xl bg-[#10B981]/10 p-3">
                  <a.icon size={22} className="text-[#10B981]" />
                </div>
                <h4 className="mt-4 font-semibold font-display text-[#0F172A]">{a.title}</h4>
                <p className="mt-2 text-sm text-[#475569]">{a.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FINAL */}
      <section className="py-20 md:py-24 bg-white">
        <Container>
          <div className="rounded-3xl border border-[#E2E8F0] bg-[#F8FAFC] p-10 md:p-14 text-center">
            <h2 className="text-3xl font-bold font-display text-[#0F172A] text-balance">
              Your website should not demand attention. It should just work.
            </h2>
            <p className="mt-4 text-lg text-[#475569] max-w-2xl mx-auto">
              Choose the level of technical responsibility you want to keep or delegate. The selected plan and accepted scope control what happens next.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <PrimaryLink to={link("/contact")} testId="about-final-cta">
                Contact us today
              </PrimaryLink>
              <SecondaryLink to={link("/plans")} testId="about-final-plans">
                View Plans
              </SecondaryLink>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
